import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { addDays, isBefore, isSameDay, startOfDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../../common/IfComponent';
import { RootState } from '../../../store';
import { toDateString } from '../../../utils';
import { fetchPricelistPeriods, recalculatePrices, showRecalculateDialog } from '../redux/calculationSlices';
const mapStateToProps = (state: RootState) => ({
    isRecalculateDialogVisible: state.calculation.showRecalculateDialog,
    pricelistPeriodDates: state.calculation.pricelistPeriodDates
});

type OwnProps = {
    pricelistId: number;
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const RecalculatePricesDialog = ({
    isRecalculateDialogVisible,
    pricelistId,
    pricelistPeriodDates
}: Props) => {
    const dispatch = useDispatch();
    const [fromDate, setFromDate] = useState(null as Date | null);
    useEffect(() => {
        dispatch(fetchPricelistPeriods.request([pricelistId]));
    }, [pricelistId, dispatch]);

    const hideDialog = () => {
        dispatch(showRecalculateDialog(false));
    };
    const handleSubmit = () => {
        dispatch(
            recalculatePrices.request({
                pricelistId: pricelistId,
                fromDate: toDateString(fromDate)
            })
        );
        hideDialog();
    };

    const minDate = useMemo(() => startOfDay(addDays(new Date(), 1)), []);

    const shouldDisableDate = (date: unknown) => {
        if (date == null) {
            return false;
        }

        return (
            isBefore(date as number | Date, minDate) ||
            pricelistPeriodDates.dates.every(
                (validFrom: string | number | Date) => !isSameDay(new Date(validFrom), date as Date)
            )
        );
    };

    return (
        <Dialog
            onClose={hideDialog}
            aria-labelledby="simple-dialog-title"
            open={isRecalculateDialogVisible}
            maxWidth="sm"
        >
            <DialogTitle id="simple-dialog-title">Recalculate prices</DialogTitle>
            <Box component="form" name="recalculatePricesForm" onSubmit={handleSubmit}>
                <DialogContent>
                    <If
                        condition={pricelistPeriodDates.isLoading}
                        then={
                            <Box
                                component="div"
                                sx={{
                                    display: 'flex',
                                    height: '100%',
                                    margin: '0 -8px',
                                    zIndex: 100,
                                    '& .MuiLinearProgress-root': {
                                        alignSelf: 'flex-end',
                                        width: '100%',
                                        height: 2,
                                        '&.MuiLinearProgress-colorPrimary': {
                                            backgroundColor: '#66afe9'
                                        }
                                    }
                                }}
                            >
                                <LinearProgress />
                            </Box>
                        }
                        else={
                            <DatePicker
                                label="Valid from"
                                inputFormat="yyyy-MM-dd"
                                openTo="day"
                                shouldDisableDate={shouldDisableDate}
                                disablePast={true}
                                value={fromDate}
                                onChange={date => setFromDate(date as Date)}
                                renderInput={params => <TextField size="small" {...params} />}
                            />
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => hideDialog()} color="primary">
                        Close
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={pricelistPeriodDates.isLoading}
                        type="submit"
                        color="primary"
                        form="recalculatePricesForm"
                    >
                        Recalculate
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
export default connect(mapStateToProps)(RecalculatePricesDialog);
